<template>
    <div class="picker-container" :class="{ open: isOpen }">
        <date-range-picker
            ref="picker"
            v-model="dateRange"
            class="custom-date-range"
            :show-dropdowns="true"
            :time-picker="false"
            :ranges="false"
            :append-to-body="true"
            :linked-calendars="false"
            :opens="opens"
            @update="applyDate"
            @toggle="onToggle($event)"
            @start-selection="startSelection($event)"
            @finish-selection="endSelection($event)"
        >
            <template v-slot:input="picker">
                <div class="selected-date-container">
                    <div class="text">From</div>
                    <div class="date">
                        {{
                            formattedDates.startDate
                                ? formattedDates.startDate
                                : $translate('add_date')
                        }}
                    </div>
                </div>
                <div class="selected-date-container">
                    <div class="text">To</div>
                    <div class="date">
                        {{
                            formattedDates.endDate ? formattedDates.endDate : $translate('add_date')
                        }}
                    </div>
                </div>
            </template>
            <div slot="header" class="custom-date-range-header">
                <div class="date-container">
                    <div>Select dates</div>
                    <div class="selected-date-header">
                        <div
                            class="selected-date-container"
                            :class="{ active: activeInput === INPUT_TYPE.START }"
                        >
                            <div class="text">From</div>
                            <div class="date">
                                <input
                                    v-model="inputStartDate"
                                    type="text"
                                    :placeholder="
                                        activeInput === INPUT_TYPE.START
                                            ? DATE_FORMAT
                                            : $translate('add_date')
                                    "
                                    @focus="activeInput = INPUT_TYPE.START"
                                    @blur="activeInput = null"
                                />
                            </div>
                        </div>
                        <div
                            class="selected-date-container"
                            :class="{ active: activeInput === INPUT_TYPE.END }"
                        >
                            <div class="text">To</div>
                            <div class="date">
                                <input
                                    v-model="inputEndDate"
                                    type="text"
                                    :placeholder="
                                        activeInput === INPUT_TYPE.END
                                            ? DATE_FORMAT
                                            : $translate('add_date')
                                    "
                                    @focus="activeInput = INPUT_TYPE.END"
                                    @blur="activeInput = null"
                                    @keydown.enter.stop="applyDate"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="date-range-container">
                    <span
                        v-for="(_, key) in ranges"
                        :key="key"
                        class="tag"
                        :class="{ active: selectedRangeKey === key }"
                        @click="selectDateRange(key)"
                    >
                        {{ key }}
                    </span>
                </div>
            </div>
            <template #date="data">
                <div>{{ data.date | dateCell }}</div>
            </template>
            <div slot="footer" class="custom-date-range-footer">
                <div>
                    <button class="btn btn-transparent" @click="clearDate">Clear dates</button>
                    <button class="btn btn-primary" @click="applyDate">Save</button>
                </div>
            </div>
        </date-range-picker>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import moment from 'moment'
import Utils from '../../../business/utils/Utils'
import { DATE_FORMAT } from '../../../business/const'

export default {
    name: 'CustomDateRangePicker',
    components: { DateRangePicker },
    filters: {
        dateCell(value) {
            return moment(value).get('date')
        },
        date(val) {
            return val ? val.toLocaleString() : ''
        }
    },
    props: {
        selectedDate: {
            type: Object,
            default: () => ({})
        },
        opens: {
            type: String,
            default: 'right'
        }
    },
    data() {
        return {
            INPUT_TYPE: {
                START: 'start',
                END: 'end'
            },
            dateRange: { ...this.selectedDate },
            inputStartDate: null,
            inputEndDate: null,
            ranges: Utils.getPastSliderDateRange(),
            isOpen: false,
            activeInput: null,
            DATE_FORMAT
        }
    },
    computed: {
        formattedDates() {
            return this.getFormattedDates(this.selectedDate)
        },
        selectedRangeKey() {
            if (this.dateRange && this.dateRange.startDate && this.dateRange.endDate) {
                for (const key in this.ranges) {
                    const [startDate, endDate] = this.ranges[key]
                    if (
                        moment(this.dateRange.startDate).isSame(startDate, 'date') &&
                        moment(this.dateRange.endDate).isSame(endDate, 'date')
                    ) {
                        return key
                    }
                }
            }
            return null
        }
    },
    watch: {
        dateRange() {
            const { startDate, endDate } = this.getFormattedDates(this.dateRange)
            this.inputStartDate = startDate
            this.inputEndDate = endDate
        },
        inputStartDate(newDate) {
            if (newDate && moment(newDate, DATE_FORMAT, true).isValid()) {
                this.dateRange.startDate = moment(newDate, DATE_FORMAT).toDate()
            }
        },
        inputEndDate(newDate) {
            if (newDate && moment(newDate, DATE_FORMAT, true).isValid()) {
                this.dateRange.endDate = moment(newDate, DATE_FORMAT).toDate()
            }
        }
    },
    methods: {
        getFormattedDates(selectedDate) {
            return {
                startDate:
                    selectedDate && selectedDate.startDate
                        ? moment(selectedDate.startDate).format(DATE_FORMAT)
                        : null,
                endDate:
                    selectedDate && selectedDate.endDate
                        ? moment(selectedDate.endDate).format(DATE_FORMAT)
                        : null
            }
        },
        onToggle(event) {
            this.isOpen = event
            this.$refs.picker.togglePicker(event)
            if (event) {
                this.dateRange = { ...this.selectedDate }
            }
            window['shrey'] = this
        },
        startSelection(newDate) {
            this.inputStartDate = moment(newDate).format(DATE_FORMAT)
        },
        selectDateRange(index) {
            const [startDate, endDate] = this.ranges[index]
            this.dateRange = { startDate, endDate }
            this.$refs.picker.clickRange(this.ranges[index])
        },
        applyDate() {
            this.$emit('apply', { ...this.dateRange })
            this.onToggle(false)
        },
        endSelection(newDate) {
            this.inputEndDate = moment(newDate).format(DATE_FORMAT)
        },
        clearDate() {
            this.dateRange = { startDate: null, endDate: null }
        }
    }
}
</script>

<style lang="scss">
.picker-container {
    &.open {
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 30;
        }
    }
}
.custom-date-range {
    height: 100%;
    .reportrange-text {
        display: flex;
        align-items: center;
        color: $white;
        width: 15rem;
        height: 100%;
        background-color: transparent !important;
        border-radius: 0 !important;
        padding: 0 !important;
        border: none !important;
    }
}
.selected-date-container {
    flex: 1;
    border: 1px solid $blue;
    padding: 0.5rem 0.75rem;
    $__border-radius: 5px;

    &:first-child {
        border-top-left-radius: $__border-radius;
        border-bottom-left-radius: $__border-radius;
        border-right-style: none;
    }
    &:last-child {
        border-top-right-radius: $__border-radius;
        border-bottom-right-radius: $__border-radius;
    }

    .text {
        font-size: 0.75rem;
        color: $light;
    }
    .date {
        font-size: 1rem;
        color: $blue;
        input {
            width: 100%;
            border: none;
            outline: none;
            color: inherit;
            background: transparent;
            appearance: none;
        }
    }
}
.daterangepicker {
    //color: $dark;
    background-color: $dark-4;
    border-color: $dark;

    .date-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;

        .selected-date-header {
            display: flex;
            width: 15rem;

            .selected-date-container {
                &:not(.active) {
                    border-color: $light;

                    .date {
                        color: $light;
                    }
                }

                &.active {
                    border-color: $white;
                    .date,
                    .text {
                        color: $white;
                    }
                }

                &:first-child.active {
                    border-right-style: solid;
                }
            }
        }
    }

    .date-range-container {
        display: flex;
        align-content: stretch;
        justify-content: space-around;
        align-items: stretch;
        height: 2.5rem;
        flex-wrap: wrap;
        padding: 0 0.5rem;

        .tag {
            color: $light;
            border: 1px solid $light;
            border-radius: 4px;
            padding: 0 1rem;
            display: grid;
            align-items: center;
            cursor: pointer;

            &:hover {
                border-color: $white;
                color: $white;
            }

            &.active {
                border-color: $blue;
                color: $blue;
            }
        }
    }

    .drp-buttons {
        border-color: $dark;
    }

    .ranges {
        li:hover {
            background-color: $black;
            color: $white;
        }
        li.active {
            background-color: $black;
            color: $blue;
        }
    }

    .calendars-container {
        background-color: transparent;
        .drp-calendar.left {
            border-color: $dark;
        }
    }

    .custom-date-range-footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding: 1rem 1.5rem;

        .btn {
            padding: 0.5rem 1.5rem !important;
        }

        .btn-primary {
            background-color: $blue;
            color: $black !important;
            font-weight: bold !important;
            border-color: $blue;
        }
        .btn-transparent {
            background-color: transparent;
            color: $white;
        }
    }

    .calendar-table {
        background-color: transparent;
        border-style: none;

        .monthselect,
        .yearselect {
            background-color: $dark-4;
            color: $light;
        }
        .prev,
        .next {
            span {
                border-color: $light;
            }
        }
        .table-condensed {
            td {
                padding: 0 !important;
                border: 0 !important;
                background-color: transparent;

                &.today {
                    font-weight: normal !important;
                }
                div {
                    aspect-ratio: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
            }
            th {
                background-color: transparent;
                color: $light;
            }
            .off {
                color: $light-alpha;
            }
            .in-range div {
                background-color: $dark-blue-alpha;
                color: $white;
            }
            .start-date,
            .end-date {
                &:not(.off) {
                    div {
                        background-color: $blue;
                        color: $black;
                        font-weight: bold;
                        border-radius: 50%;
                        position: relative;
                    }
                }
            }
            .start-date {
                &:not(.off) {
                    div:after {
                        content: '';
                        background-color: $dark-blue-alpha;
                        position: absolute;
                        top: 0;
                        left: 50%;
                        right: 0;
                        bottom: 0;
                        z-index: -1;
                    }
                }
            }
            .end-date {
                &:not(.off) {
                    div:after {
                        content: '';
                        background-color: $dark-blue-alpha;
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 50%;
                        bottom: 0;
                        z-index: -1;
                    }
                }
            }
        }
    }
}
</style>
